import { AxiosResponse } from "axios";
import store from "store"
import MD5 from "crypto-js/md5";
import trans from "trans"

import { initialGuidesState, setActiveGroupId } from "../reducers";
import { UPLOAD_IMAGE_FILE } from 'template-edit/reducers';
import { createToast, setGuides } from "actions";
import toastFactory, { ResponseErrorType, textToast } from "app/helpers/toast_factory";

import {
    AlternativeTemplate,
    AlternativeTemplatesLevels,
    AnyPoint,
    CollageLayoutImageType,
    CollageLayoutType,
    EffectsType,
    emptyEffects,
    FormNameType,
    Gradient,
    shapeCheckFields,
    Size,
    SizeType,
    Template,
    templateCheckFields
} from "app/types/model-types";

import { ACTIVE_VIEW_NEW_SHAPE, ActiveViewType } from "template-edit/containers/Editor";

import LayerGroupType from "shape-entities/types/layerGroup";
import PathShape, { PathPoint } from "shape-entities/types/PathShape";
import PolygonShape from "shape-entities/types/PolygonShape";
import { CoordsType, CoordsWithMiddleType } from "shape-entities/components/shape";

import { defaultFontName, getSourceImage } from "shape-entities/defaults";
import { RandomProduct } from '../types';

import Shape, { BADGE_TYPE, DASHED_FRAME_TYPE, DYNAMIC_TEXT_TYPE, getFormattedValue, IMAGE_FROM_ELEMENTS_TYPE, RATING_TYPE, SOURCE_IMAGE_TYPE, TEXT_WITH_ELEMENTS_TYPE, TextXmlElementFormatter, TRIANGLE1_TYPE, TRIANGLE2_TYPE } from 'shape-entities/types/shape';
import { ID, intboolean, ReduxList } from "app/types/app-types";
import uniqueId from "app/helpers/unique_id";
import { projectId } from "app/helpers/api";
import { TemplateGalleryType } from "template-edit/components/template-gallery";
import LineShape from "shape-entities/types/LineShape";
import { TemplateOrAlternativeTemplateType, UndoRedoHistory } from "template-edit/undoRedoReducers";
import { URL } from "app/config/api-config";
import CurvedTextShape from "shape-entities/types/CurvedTextShape";
import TextInsideShape from "shape-entities/types/TextInsideShape";
import { initForm, selectFormValues, updateInputValue } from "app/reducers/form";
import { DEFAULT_PATH_TYPE } from "shape-entities/components/path-svg";
import { SizeItem } from "settings/components/sizes-settings";
import { AITemplatType, FindSimilarType, InpaintingGroupType } from "products/types";
import newTemplate from "template-edit/helpers/newTemplate";

import { sortBy, forEach, find, intersection, indexOf } from "lodash";
import { BRUSH_MIN_SPACING } from "shape-entities/components/brush-svg";
import { DEFAULT_CURSOR, ROTATE_CURSOR } from "template-edit/containers/image-preview";
import { isJson } from "app/helpers/other";
import { RouterType } from "app/components/routes";
import { LEVELS_MODE_ALL, LEVELS_MODE_FIRST, LEVELS_MODE_LAST, LEVELS_MODE_SELECTED, LevelsModeType } from "template-edit/components/save-template-form";

export const MAX_FILE_SIZE_KB = 1024
export const MAX_UPLOAD_FILE_PX = 4e+6      //max width x height
export const ALLOWED_FILE_TYPES = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/svg",
    "image/webp",
    "image/avif",
    "image/svg+xml",
]

const { abs, sin, cos, PI, max, min, round, atan2, atan, sqrt, floor } = Math

export const staticElementPriorityOffset = 200
export const SOURCE_IMAGE_INDEX = 'sourceImage'

export type RootState = ReturnType<typeof store.getState>

export type StateType = RootState & {
    isAlt: boolean
    activeShapeId: string
    selectedLayers: string[]
    lockedLayers: string[]
    activeView: ActiveViewType
    randomProduct: RandomProduct
    sizes: ReduxList<SizeItem>
    alternativeTemplatesLevels: ReduxList<AlternativeTemplatesLevels>
    router: RouterType
}

export type LineJoinType = "round" | "inherit" | "miter" | "bevel"
export type LineCapType = "round" | "inherit" | "butt" | "square"

export const DEFAULT_LINE_JOIN = 'round'
export const DEFAULT_LINE_CAP = 'round'

export const LINE_JOIN_CONSTS: LineJoinType[] = ['inherit', 'miter', 'round', 'bevel']
export const LINE_CAP_CONSTS: LineCapType[] = ['inherit', 'butt', 'round', 'square']

export const previewFile = (formName: FormNameType, elementId: string, subFormName: string, shape: Shape, change: Function, file_path: string) => {
    const w: any = window
    const _URL = w.URL || w.webkitURL;

    //get file from DOM element:
    const shapeInputEl: any = document.querySelector(`#${elementId}`)

    if (shapeInputEl) {
        let file = shapeInputEl.files[0]

        if (!file) {
            //no file,
        } else if (file.size > MAX_FILE_SIZE_KB * 1024) {
            const error: ResponseErrorType = {
                message: 'error: this file is not allowed! the file size is ' + round(file.size / 1024) + ' max. allowed file size is ' + MAX_FILE_SIZE_KB + 'kb ',
                response: null
            }
            store.dispatch(createToast(toastFactory(UPLOAD_IMAGE_FILE.ERROR, error)))
        } else if (!ALLOWED_FILE_TYPES.includes(file.type)) {
            const error: ResponseErrorType = {
                message: 'error: this file (' + file.type + ') is not allowed! Allowed types are: ' + ALLOWED_FILE_TYPES.join(','),
                response: null
            }
            store.dispatch(createToast(toastFactory(UPLOAD_IMAGE_FILE.ERROR, error)))
        } else {
            let reader = new FileReader()
            let img = new Image()

            reader.onloadend = () => {
                img.onload = () => {
                    if (typeof reader.result === "string") {
                        const newShape: Shape = {
                            ...shape,
                            width: img.width,
                            height: img.height,
                            imageBlob: reader.result,
                            file: file_path,
                        }
                        change(formName, subFormName, newShape)
                    }
                }
            }

            if (file) {
                reader.readAsDataURL(file) //reads the data as a URL
                img.src = _URL.createObjectURL(file)
            }
        }
    } else {
        console.debug('cannot find element by id', `#${elementId}`)
    }
}

export function intersect(a, b) {
    return [...new Set(a)].filter(x => new Set(b).has(x));
}

export function createGroupFromSelected() {
    const state = store.getState() as StateType

    const form: TemplateOrAlternativeTemplateType = selectFormValues(state, getFormName(state.isAlt))
    const formName = getFormName(state.isAlt)

    const selectedLayers: string[] = state.selectedLayers

    if (form.shapes && selectedLayers && selectedLayers.length > 1) {

        const orderedByPriority = sortBy(form.shapes, [(shape) => {
            return shape.priority
        }]).filter(shape => (selectedLayers.includes(shape.index)))

        const names = orderedByPriority.map(shape => trans(shape.type))
        const indexes = orderedByPriority.map(shape => shape.index)

        const groupIndex = MD5(`${Date.now()}`).toString()

        //if new layer group contain something from other group, remove other group.
        //keep flat structure!
        const oldGroups = form.groups ? [...form.groups].filter(g => {
            return intersection(g.list, indexes).length < 1
        }) : []

        const newLayerGroup: LayerGroupType = {
            'index': groupIndex,
            'list': indexes,
            'name': '(' + indexes.length + ') ' + names.join(', '),
        }

        const groups = [...oldGroups ?? [], newLayerGroup]

        store.dispatch(setActiveGroupId(groupIndex))
        store.dispatch(updateInputValue(formName, 'groups', groups))
    }
}

export function reorderShapes(shapes: Shape[], newIndexOrder: string[]) {
    const state = store.getState() as StateType
    const isAlt = state.isAlt
    const activeView: ActiveViewType = state.activeView
    const formName = getFormName(isAlt)

    let dynamicElements = []
    let staticElements = []

    let priority = 0
    let parsed = []

    let prevShapePriority = null;

    let sourceImageShape = find(shapes, (shape: Shape) => {
        return shape.index === SOURCE_IMAGE_INDEX
    })

    const firstStaticShape = find(shapes, (shape: Shape) => {
        return !shape.is_dynamic && shape.type !== SOURCE_IMAGE_INDEX
    })

    if (typeof firstStaticShape === "undefined" && sourceImageShape) {
        sourceImageShape.priority = staticElementPriorityOffset + 1;
        sourceImageShape.is_dynamic = 0
    }

    //the source image can be both static or dynamic!
    //if the shape before source image is dynamic then it is dynamic too:

    newIndexOrder.forEach((index, id) => {
        try {
            const array = JSON.parse(index)
            parsed.push(...array)
        } catch (e) {
            //not json, it is single index
            parsed.push(index)
        }
    })

    parsed.forEach((index, id) => {
        priority++

        const shape = find(shapes, (shape: Shape) => {
            return shape.index === index
        })

        if (shape) {
            if (
                (shape.type !== SOURCE_IMAGE_TYPE && shape.is_dynamic) ||
                (shape.type === SOURCE_IMAGE_TYPE && shape.is_dynamic && typeof firstStaticShape !== "undefined" && prevShapePriority && prevShapePriority < staticElementPriorityOffset)
            ) {
                dynamicElements[index] = { ...shape, priority: priority, is_dynamic: 1 }
                prevShapePriority = priority
            } else {
                if (activeView !== ACTIVE_VIEW_NEW_SHAPE && shape.type === BADGE_TYPE && (typeof shape.set_file_id === "undefined" || !shape.set_file_id)) {
                    //omit shape, it is empty
                } else {
                    staticElements[index] = {
                        ...shape,
                        priority: (staticElementPriorityOffset + priority),
                        is_dynamic: 0
                    }
                    prevShapePriority = staticElementPriorityOffset + priority
                }
            }
        }
    })

    //if source image shape not exists:
    if (!sourceImageShape) {
        staticElements[SOURCE_IMAGE_INDEX] = { ...getSourceImage(), priority: (staticElementPriorityOffset + priority + 1) }    //debug
    }

    store.dispatch(updateInputValue(formName, 'shapes', { ...dynamicElements, ...staticElements }))
}

export function rotateSelectedShapes(groupDeltaAngle: number, middleX: number = null, middleY: number = null) {
    const state = store.getState() as StateType
    const { selectedLayers, isAlt } = state

    const form = selectFormValues(state, getFormName(state.isAlt))
    const formName = getFormName(isAlt)

    if (!middleX || !middleY) {
        const calculated: GroupSizeAndPosition = calculateGroupSizeAndPosition()
        middleX = calculated.middleX
        middleY = calculated.middleY
    }

    let newShapes = [];
    const orderedShapes = sortBy(Object.keys(form.shapes).map(o => form.shapes[o]), [o => new Number(o.priority)])
    orderedShapes.forEach((shape) => {
        if (selectedLayers.includes(shape.index)) {
            const A = shape.x - middleX
            const B = shape.y - middleY

            const alpha = groupDeltaAngle / 180 * PI

            newShapes[shape.index] = {
                ...shape,
                x: round(middleX + A * cos(alpha) - B * sin(alpha)),
                y: round(middleY + A * sin(alpha) + B * cos(alpha)),
                angle: round((shape.angle || 0) + groupDeltaAngle) % 360,
            }
        } else {
            newShapes[shape.index] = { ...shape }
        }
    })

    store.dispatch(updateInputValue(formName, "shapes", {
        ...form.shapes,
        ...newShapes
    }))
}

export type GroupSizeAndPosition = {
    groupWidth: number
    groupHeight: number
    middleX: number
    middleY: number
    middleXInRotatedCoords: number
    middleYInRotatedCoords: number
    groupAngle: number
}

export const initCalculatedGroupSizeAndPosition: GroupSizeAndPosition = {
    groupAngle: 0,
    groupWidth: null,
    groupHeight: null,
    middleX: null,
    middleY: null,
    middleXInRotatedCoords: null,
    middleYInRotatedCoords: null,
}

export function calculateGroupSizeAndPosition(): GroupSizeAndPosition {
    const state = store.getState() as StateType
    const { selectedLayers } = state

    const form = selectFormValues(state, getFormName(state.isAlt))

    if (!selectedLayers || selectedLayers.length < 1 || !form || !form.shapes || form.shapes.length < 1) {
        return { ...initCalculatedGroupSizeAndPosition }
    }

    //1. find upper most shape or common shape angle
    //get uppper most shape after position transformed:
    let upperMostShape = null
    forEach(form.shapes, shape => {
        if (shape && selectedLayers.includes(shape.index)) {
            if (!upperMostShape) {
                upperMostShape = shape
                upperMostShape.angle = upperMostShape.angle || 0
            } else {
                if (shape.y < upperMostShape.y) {
                    upperMostShape = shape
                    upperMostShape.angle = upperMostShape.angle || 0
                }
            }
        }
    })

    let groupAngle = upperMostShape.angle

    /* //angle distribution:
    let shapeAngles = {}
    forEach(templateForm.shapes, shape => {
        if (shape && selectedLayers.includes(shape.index)) {
            const angle = shape.angle || 0
            shapeAngles[shape.angle] = {
                angle: angle,
                count: (shapeAngles[angle]?.count || 0) + 1  //count plus one
            }
        }
    })

    const sortedByCount = sortBy(shapeAngles, 'count')
    const theMostCommonAngleAndCount = sortedByCount[sortedByCount.length - 1]
    if (theMostCommonAngleAndCount && theMostCommonAngleAndCount['count'] > 1) {
        groupAngle = theMostCommonAngleAndCount['angle']
    } */

    const middleX = 0
    const middleY = 0

    let minX = null
    let minY = null
    let maxX = null
    let maxY = null

    //2. find shapes boundaries
    forEach(form.shapes, shape => {
        if (selectedLayers.includes(shape.index)) {
            getRotatedShapeInSpace(shape, 360 - groupAngle, middleX, middleY).forEach(pos => {
                minX = min(pos.x, minX || pos.x)
                maxX = max(pos.x, maxX || pos.x)
                minY = min(pos.y, minY || pos.y)
                maxY = max(pos.y, maxY || pos.y)
            })
        }
    })

    const groupWidth = abs(minX - maxX)
    const groupHeight = abs(minY - maxY)

    const middleXInRotatedCoords = round((maxX + minX) / 2)
    const middleYInRotatedCoords = round((maxY + minY) / 2)

    //rotate middle coords back to groupAngle.angle using same middle point [middleX, middleY]
    const alpha = groupAngle / 180 * PI
    const c = cos(alpha)
    const s = sin(alpha)

    const A = middleXInRotatedCoords - middleX
    const B = middleYInRotatedCoords - middleY

    const newMiddleX = round(middleX + A * c - B * s)
    const newMiddleY = round(middleY + A * s + B * c)

    return {
        groupWidth: groupWidth,
        groupHeight: groupHeight,
        middleX: newMiddleX,
        middleY: newMiddleY,
        middleXInRotatedCoords,
        middleYInRotatedCoords,
        groupAngle: groupAngle,
    }
}

export function findCentroid(points: CoordsType[]): { middleX: number, middleY: number } {
    let middleX = 0,
        middleY = 0

    points.forEach(point => {
        middleX += point.x
        middleY += point.y
    })

    return {
        middleX: middleX / points.length,
        middleY: middleY / points.length
    }
}

export function sortVerticies(points) {
    const { middleX, middleY } = findCentroid(points)
    //TODO: switch cx,cy -> cx2,cy2 if ....

    return sortBy(points, [(point: PathPoint) => {
        return ((atan2(point.x - middleX, point.y - middleY)) * 180 / PI + 360) % 360
    }])
}

export function getSubdividedPoints(points: AnyPoint[], spacing: number): AnyPoint[] {
    if (spacing <= BRUSH_MIN_SPACING) {
        return points;
    }

    //draw path
    const ll = points.map(p => `${p.x} ${p.y}`).join(' ')
    const d = `M ${points[0].x} ${points[0].y} L${ll}`

    const p = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    p.setAttribute('d', d)

    //subdivide path with the constant spacing
    const totalLineLength = p.getTotalLength()
    const count = floor(totalLineLength / spacing) + 1
    const updatedSpacing = totalLineLength / count

    return [{   //keep first point!
        x: points[0].x,
        y: points[0].y,
    }, ...Array.from(Array(count), (_, i) => i + 1).map((i) => {
        const point = p.getPointAtLength(i * updatedSpacing)
        return {
            x: round(point.x),
            y: round(point.y)
        }
    })]
}

export function getRotatedShapeInSpace(shape: Shape, beta: number = 0, middleX = 0, middleY = 0) {
    const shapeSize = getShapeSize(shape)

    const alpha = beta / 180 * PI

    const A = shape.x - middleX
    const B = shape.y - middleY

    const deltaX = shape.x - (middleX + A * cos(alpha) - B * sin(alpha))
    const deltaY = shape.y - (middleY + A * sin(alpha) + B * cos(alpha))

    const newXpos = round(shape.x - deltaX)
    const newYpos = round(shape.y - deltaY)

    const halfWidth = .5 * (shapeSize.width) * (shape.scale || 1)
    const halfHeight = .5 * (shapeSize.height) * (shape.scale || 1)

    const newAlpha = ((shape.angle || 0) + beta) / 180 * PI

    const c = cos(newAlpha);
    const s = sin(newAlpha);

    const r1x = -halfWidth * c - halfHeight * s;
    const r1y = -halfWidth * s + halfHeight * c;
    const r2x = halfWidth * c - halfHeight * s;
    const r2y = halfWidth * s + halfHeight * c;

    return [
        { x: newXpos + r1x, y: newYpos + r1y },
        { x: newXpos - r1x, y: newYpos - r1y },
        { x: newXpos + r2x, y: newYpos + r2y },
        { x: newXpos - r2x, y: newYpos - r2y }
    ]
}

export function getRotatedShapeSize(width: number, height: number, angle: number): SizeType {
    if (!angle) {
        return { width, height }
    }

    const alpha = angle / 180 * PI

    const c = cos(alpha)
    const s = sin(alpha)

    return {
        width: round(width * c + height * s),
        height: round(width * s + height * c)
    }
}

export function getRotatedRectangleInSpace(width: number, height: number, angle: number, scaleX: number = 1, scaleY: number = 1): AnyPoint[] {
    const alpha = angle / 180 * PI

    const halfWidth = .5 * width
    const halfHeight = .5 * height

    const points = [
        { x: 0, y: 0 },
        { x: 0, y: height },
        { x: width, y: height },
        { x: width, y: 0 }
    ]

    const c = cos(alpha)
    const s = sin(alpha)

    const cx = halfWidth
    const cy = halfHeight

    const rotatedPoints = points.map((point: AnyPoint) => {
        const A = point.x - cx
        const B = point.y - cy

        const x = A * c - B * s
        const y = A * s + B * c

        return { x, y }
    })

    return rotatedPoints.map((point: AnyPoint) => {
        return {
            x: round(point.x * scaleX),
            y: round(point.y * scaleY)
        }
    })
}

export function getScaledShapeSizeAndAngle(width: number, height: number, scaleX: number, scaleY: number, angle: number): { width: number, height: number, angle: number } {
    return {
        width: round(width * scaleX),
        height: round(height * scaleY),
        angle: angle,
    }

    if (!angle) {
        return {
            width: round(width * scaleX),
            height: round(height * scaleY),
            angle: angle,
        }
    } else if (angle === 90) {
        return {
            width: round(width * scaleX),
            height: round(height * scaleY),
            angle: angle,
        }
    }

    const p = getRotatedRectangleInSpace(width, height, angle, scaleX, scaleY)

    const k = (p[2].y - p[1].y) / (p[2].x - p[1].x)
    const l = (p[1].y - p[0].y) / (p[1].x - p[0].x)

    const q1 = p[1].y - k * p[1].x
    const q2 = p[3].y - k * p[3].x

    const m1 = p[0].y - l * p[0].x
    const m2 = p[3].y - l * p[3].x

    const newAngle = round(atan(k) * 180 / PI)
    const newWidth = round(abs(m2 - m1) / sqrt(1 + l * l))
    const newHeight = round(abs(q2 - q1) / sqrt(1 + k * k))

    //console.debug(`scale: ${scaleX}, ${scaleY}, ${width}x${height} --> ${newWidth}x${newHeight},  angle ${angle} --> ${newAngle}`)
    //console.debug(`${k}*x+(${q1}), ${k}*x+(${q2}), ${l}*x+(${m1}), ${l}*x+${m2}`, p)
    //console.debug(`rectangle`, getRotatedShapeSize(width, height, angle))

    return {
        width: newWidth,
        height: newHeight,
        angle: newAngle,
    }
}

export function getTemplateSize(thisTemplateForm: TemplateOrAlternativeTemplateType = null): SizeType {
    const state = store.getState() as StateType
    const { randomProduct } = state

    const form = thisTemplateForm ? thisTemplateForm : selectFormValues(state, getFormName(state.isAlt))

    if (!form.is_keep_size && form.size?.width && form.size?.height) {
        return {
            width: form.size.width,
            height: form.size.height,
        }
    }

    return {
        width: randomProduct?.width || 1080,
        height: randomProduct?.height || 1080,
    }
}

export function getRotateHandleSize(): number {
    const state = store.getState() as StateType
    if (!state) {
        return 2
    }

    const templateSize = getTemplateSize()
    const rotateHandleSize = round(min(templateSize.width, templateSize.height) / 50)

    if (rotateHandleSize > 40) {
        return 40
    }

    if (rotateHandleSize < 2) {
        return 2
    }
    return rotateHandleSize
}

export function getHandleSize(): number {
    const state = store.getState() as StateType
    if (!state) {
        return 10
    }

    const templateSize = getTemplateSize()
    const handleSize = round(max(templateSize.width, templateSize.height) / 120)

    return (handleSize > 5 && handleSize < 35) ? handleSize : 10
}

/**
 * Get min approach for guidelines from the template size.
 */
export function getMinApproachFromSize(width: number, height: number): { x: number, y: number } {
    let minApproachX = round(width / 100)
    let minApproachY = round(height / 100)

    if (minApproachX < 3) {
        minApproachX = 3
    }

    if (minApproachY < 3) {
        minApproachY = 3
    }

    return { x: minApproachX, y: minApproachY }
}

export function getGridMinApproachPx(gridStepPx: number): number {
    return .25 * gridStepPx
}

export const GRID_MODES_COUNT = 4
export function getGridStepSize(width: number, height: number, mode: number): number {
    switch (mode) {
        case 0: //do not show grid
        case 1:
            return width / 10
        case 2:
            return width / 6
        case 3:
            return width / 4
        default:
            return 100
    }
}

//uppermost left point not necessary part of shape if rotated
export function calcObjectCorners(x: number, y: number, width: number, height: number, angle: number = 0): CoordsWithMiddleType {
    const alpha = (angle || 0) / 180 * PI

    const halfWidth = .5 * (width || height)
    const halfHeight = .5 * (height || width)

    const c = cos(alpha)
    const s = sin(alpha)

    const l = x - halfWidth
    const r = x + halfWidth
    const b = y - halfHeight
    const t = y + halfHeight

    const fourCorners: CoordsType[] = [
        { x: l, y: t },
        { x: l, y: b },
        { x: r, y: t },
        { x: r, y: b },
    ]

    let allRotatedPoints: CoordsType[] = []

    //start with middle point
    let minRotetedX = null
    let maxRotetedX = null
    let minRotatedY = null
    let maxRotatedY = null

    //rotate and get min x, min y, it  can be any of these four points
    forEach(fourCorners, p => {
        const A = p.x - x
        const B = p.y - y

        const rotatedX = round((x + A * c - B * s))
        const rotatedY = round((y + A * s + B * c))

        if (angle && A && B) {  //not center points
            allRotatedPoints.push({ x: rotatedX, y: rotatedY })
        }

        if (minRotetedX === null || rotatedX < minRotetedX) {
            minRotetedX = rotatedX
        }
        if (maxRotetedX === null || rotatedX > maxRotetedX) {
            maxRotetedX = rotatedX
        }

        if (minRotatedY === null || rotatedY < minRotatedY) {
            minRotatedY = rotatedY
        }
        if (maxRotatedY === null || rotatedY > maxRotatedY) {
            maxRotatedY = rotatedY
        }
    })

    return {
        topLeft: { x: minRotetedX, y: minRotatedY },
        bottomRight: { x: maxRotetedX, y: maxRotatedY },
        middle: { x, y },
        allRotatedPoints: allRotatedPoints,
        angle: angle || 0,
    }
}

export function recalcShapeSizeAndPointsPositions(form: TemplateOrAlternativeTemplateType, shape: PathShape | PolygonShape | LineShape | CurvedTextShape | TextInsideShape, pathElementId: string = null): PathShape | PolygonShape | LineShape | CurvedTextShape | TextInsideShape {
    if (shape.points.length < 1) {
        return shape
    }

    const clonedPoints = JSON.parse(JSON.stringify([...shape.points]))

    if (pathElementId) {
        const templateSize = getTemplateSize(form)

        const boundingBoxEl = document.getElementById(pathElementId)
        const middleShapeEl = document.getElementById(`middle-shape-${shape.index}`)
        const canvasWrapperEl = document.getElementById('canvas-wrapper')
        const svgImageEl = document.getElementById('image')
        const firstPointEl = document.getElementById(`pbezier_${shape.index}_0`)

        if (boundingBoxEl && middleShapeEl && canvasWrapperEl && svgImageEl && firstPointEl) {
            console.debug('TODO:')
            /* const boundingBox = boundingBoxEl.getBoundingClientRect()
            const svgImageBoundingBox = svgImageEl.getBoundingClientRect()
            const firstPointBox = firstPointEl.getBoundingClientRect()
            if (svgImageBoundingBox.width && svgImageBoundingBox.height && templateSize.width && templateSize.height) {
                const scaleX = templateSize.width / svgImageBoundingBox.width
                const scaleY = templateSize.height / svgImageBoundingBox.height

                const widthFromElement = boundingBox.width * scaleX
                const heightFromElement = boundingBox.height * scaleY

                const firstPoint = clonedPoints[0]
                const x = (svgImageBoundingBox.left - firstPointBox.left + firstPointBox.width / 2) * scaleX
                const y = (svgImageBoundingBox.top - firstPointBox.top + firstPointBox.height / 2) * scaleY

                const xoffset = round(x + firstPoint.x + shape.x)
                const yoffset = round(y + firstPoint.y + shape.y)
            } */
        }
    }

    let minX = 0,
        minY = 0,
        maxY = 0,
        maxX = 0

    let updatedPoints = []

    shape.points.forEach((point: CoordsType | PathPoint, key: number) => {
        if (key === 0) {
            minX = point.x
            maxX = point.x
            minY = point.y
            maxY = point.y
        }

        if ('cx' in point && 'cy' in point && typeof point.cx === 'number' && typeof point.cy === 'number') {
            let Xs = [point.x, point.cx, 2 * point.x - point.cx]
            let Ys = [point.y, point.cy, 2 * point.y - point.cy]

            Xs.forEach(x => {
                if (x < minX) {
                    minX = x - abs(point.cx)
                }
                if (x > maxX) {
                    maxX = x + abs(point.cx)
                }
            })

            Ys.forEach(y => {
                if (y < minY) {
                    minY = y - abs(point.cy)
                }
                if (y > maxY) {
                    maxY = y + abs(point.cy)
                }
            })
        } else {
            if (point.x < minX) {
                minX = point.x
            }
            if (point.x > maxX) {
                maxX = point.x
            }
            if (point.y < minY) {
                minY = point.y
            }
            if (point.y > maxY) {
                maxY = point.y
            }
        }

        updatedPoints.push(point)
    })

    const middleX = round((minX + maxX) / 2)
    const middleY = round((minY + maxY) / 2)

    let translatedPoints = []
    updatedPoints.forEach(point => {
        const translatedPoint = {
            ...point,
            x: point.x - middleX,
            y: point.y - middleY,
        }

        translatedPoints.push(
            ('cx' in point && 'cy' in point && point.t !== 'A') ?  //not for arc, cx and cy are actually the radius x and radius y !!!
                {
                    ...translatedPoint,
                    cx: point.cx - middleX,
                    cy: point.cy - middleY,
                    cx2: typeof point.cx2 !== 'undefined' ? point.cx2 - middleX : undefined,
                    cy2: typeof point.cy2 !== 'undefined' ? point.cy2 - middleY : undefined,
                } : translatedPoint
        )
    })

    const alpha = ((shape.angle || 0)) / 180 * PI

    const c = 1.2
    const width = c * ((maxX - minX) > 0 ? (maxX - minX) : 1)
    const height = c * ((maxY - minY) > 0 ? (maxY - minY) : 1)

    return {
        ...shape,
        points: translatedPoints,
        width: round(width),   //put some space on the edges
        height: round(height),
        x: shape.x + round((middleX * cos(alpha) - middleY * sin(alpha))),
        y: shape.y + round((middleX * sin(alpha) + middleY * cos(alpha))),
    }
}

export function rotatePointAboutPoint(point: CoordsType, center: CoordsType, angle: number, isRounded: boolean = true): CoordsType {
    const alpha = angle / 180 * PI

    const A = point.x - center.x
    const B = point.y - center.y

    const c = cos(alpha)
    const s = sin(alpha)

    const deltaX = point.x - (center.x + A * c - B * s)
    const deltaY = point.y - (center.y + A * s + B * c)

    return {
        x: isRounded ? round(point.x - deltaX) : point.x - deltaX,
        y: isRounded ? round(point.y - deltaY) : point.y - deltaY,
    }
}

export function svgToBase64Url(svgString: string, width: number, height: number): string {
    const base64SVG = btoa(`<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${width}px" height="${height}px">${svgString}</svg>`);
    return `url('data:image/svg+xml;base64,${base64SVG}')`;
}

export function responseToast(response: AxiosResponse<any>): void {
    store.dispatch(createToast(textToast('success', response.status + ' ' + JSON.stringify(response.data))))
}

export function sortByAsNumber(arrayToSort: any[]): any[] {
    return arrayToSort.sort((a: string, b: string) => parseFloat(`${a}`) - parseFloat(`${b}`))
}

export function recalculateShapes(oldSize: Size, newSize: Size, form: TemplateOrAlternativeTemplateType) {
    console.debug('size updated, recalculating shapes...')
    if (!oldSize || !newSize || !oldSize.width || !oldSize.height || !newSize.width || !newSize.height || (oldSize.width === newSize.width && oldSize.height === newSize.height)) {
        return {
            ...form,
            size: newSize,
            size_id: newSize?.id || null,
        }
    }

    document.title = `FIE: ${form.name} ${newSize.width}x${newSize.height}`

    const shapes = form.shapes

    const coefX = newSize.width / oldSize.width
    const coefY = newSize.height / oldSize.height

    const c = (coefX + coefY) * .5

    const resizeableThicknessProps = [
        'text_decoration_linethickness',
        'text_stroke_width',
        'text_decoration_lineoverlap',
        'stroke_width',
        'text_fontsize',
    ] //min 1px or null

    let newShapes = {}
    Object.keys(shapes).forEach((index) => {
        const cx = (shapes[index].is_aspect_size_ratio ? c : coefX)
        const cy = (shapes[index].is_aspect_size_ratio ? c : coefY)

        newShapes[index] = shapes[index] as Shape

        if (index !== SOURCE_IMAGE_INDEX) {
            newShapes[index].width = newShapes[index].width ? round(cx * newShapes[index].width) : null
            newShapes[index].height = newShapes[index].height ? round(cy * newShapes[index].height) : null
            newShapes[index].x = newShapes[index].x ? round(coefX * newShapes[index].x) : 0
            newShapes[index].y = newShapes[index].y ? round(coefY * newShapes[index].y) : 0
            newShapes[index].corner_radius = newShapes[index].corner_radius ? round(c * newShapes[index].corner_radius) : null
            newShapes[index].line_spacing = newShapes[index].line_spacing ? round(c * newShapes[index].line_spacing) : null
            newShapes[index].letter_spacing = newShapes[index].letter_spacing ? round(c * newShapes[index].letter_spacing) : null

            resizeableThicknessProps.forEach((p: string) => {
                if (typeof newShapes[index][p] !== 'undefined' && newShapes[index][p]) {
                    const newValue = round(c * newShapes[index][p] / 2) * 2
                    newShapes[index][p] = newValue !== 0 ? newValue : 1
                }
            })

            if (newShapes[index].points && Array.isArray(newShapes[index].points) && newShapes[index].points.length > 0) {
                let newPoints = []
                newShapes[index].points.forEach(point => {
                    const newPoint = {
                        x: round(point.x * cx),
                        y: round(point.y * cy),
                    }
                    if ('cx' in point && 'cy' in point) {
                        newPoints.push({
                            ...newPoint,
                            cx: round(point.cx * cx),
                            cy: round(point.cy * cy),
                        })
                    } else {
                        newPoints.push({
                            ...newPoint,
                        })
                    }
                })
                newShapes[index].points = newPoints
            }
        }
    })

    const minApproach = getMinApproachFromSize(newSize.width, newSize.height)

    store.dispatch(setGuides({
        ...initialGuidesState,
        minApproachX: minApproach.x,
        minApproachY: minApproach.y,
        gridStep: getGridStepSize(newSize.width, newSize.height, initialGuidesState.gridStepMode)
    }))

    const minNewSize = min(newSize.width, newSize.height)
    const minOldSize = min(oldSize.width, oldSize.height)

    const ratio = oldSize && minOldSize ? minNewSize / minOldSize : 1
    const newOverscan = floor((form.overscan || 0) * ratio)

    const useMaxSize = form.max_image_width && form.max_image_height

    return {
        ...form,
        shapes: newShapes,
        translate_x: form.translate_x * c,
        translate_y: form.translate_y * c,
        size: newSize,
        size_id: newSize.id,
        overscan: (newOverscan <= -floor(minNewSize / 2)) ? -floor(minNewSize / 2) + 1 : newOverscan,
        max_image_width: useMaxSize ? floor((form.max_image_width) * ratio) : null,
        max_image_height: useMaxSize ? floor((form.max_image_height) * ratio) : null,
    }
}

export function stringToHash(string: string): number {
    let hash = 0;

    if (!string || string.length === 0) {
        return hash;
    }

    for (let i = 0; i < string.length; i++) {
        const char = string.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
    }

    return hash;
}

export const getElementValue = (serialized_data: any[], element: string, maySkipOrAddNumbers: boolean = true): string => {
    let replaced = serialized_data
    if (!serialized_data) {
        return ''
    }
    element.split('.').forEach(p => {
        if (maySkipOrAddNumbers && !Number.isInteger(p) && typeof replaced[p] === "undefined" && typeof replaced[0] !== "undefined") {
            replaced = replaced[0]
        }

        replaced = typeof replaced[p] !== "undefined" ? replaced[p] : ''
    })
    return typeof replaced === "string" || typeof replaced === "number" ? `${replaced}` : JSON.stringify(serialized_data[element]);
}

/**
 * Replaces dynamic text content with random product data
 */
export function replaceDynamicTextWithProductData(form: TemplateOrAlternativeTemplateType, randomProduct: RandomProduct) {
    let shapes: Shape[] = form.shapes;

    if (randomProduct && randomProduct.serialized_data && JSON.stringify(randomProduct.serialized_data) !== '') {
        forEach(shapes, (shape) => {
            if (shape.is_dynamic) {
                if (shape.type === TEXT_WITH_ELEMENTS_TYPE && shape.text_content) {
                    let replaced = shape.text_content

                    const matches = shape.text_content.match(/%([\w-:|.]+)%/g)
                    if (matches) {
                        matches.forEach((element) => {
                            const trimmed = element.substring(1, element.length - 1)
                            const trimmedEscaped = trimmed.replace('|', '\\|')
                            const regex = new RegExp("%" + trimmedEscaped + "%", "g")
                            const elementValue = getElementValue(randomProduct.serialized_data, trimmed)
                            replaced = replaced.replace(regex, elementValue)
                        });
                    }

                    shape.text_content_replaced = replaced
                } else if (shape.type === RATING_TYPE) {
                    let ratingCount = null
                    let elementValue = null
                    if (shape.rating_from_depots) {
                        elementValue = `${randomProduct.depotsRatingAvg}`
                        ratingCount = randomProduct.depotsRatingCount
                    } else if (shape.rating_xml_element) {
                        elementValue = getElementValue(randomProduct.serialized_data, shape.rating_xml_element)
                        ratingCount = shape.rating_count_xml_element ? getElementValue(randomProduct.serialized_data, shape.rating_count_xml_element) : ''
                    }

                    shape.text_content_replaced = ratingCount ? `${elementValue}_${ratingCount}` : elementValue
                } else if (shape.type === DYNAMIC_TEXT_TYPE && shape.text_xml_element) {
                    const elementValue = getElementValue(randomProduct.serialized_data, shape.text_xml_element)
                    const f: TextXmlElementFormatter = typeof shape.text_xml_element_formatter === 'string' && isJson(shape.text_xml_element_formatter) ? JSON.parse(shape.text_xml_element_formatter) : null
                    const formattedValue = shape.text_xml_element_formatter && f ? getFormattedValue(f, elementValue) : elementValue
                    shape.text_content = formattedValue
                }
            }
        })
    }

    return shapes;
}

export function getClonedShapes(shapes: Shape[], newTemplateId: ID): Shape[] {
    let newShapes = JSON.parse(JSON.stringify(shapes))

    Object.keys(shapes).forEach((key) => {
        const shape = shapes[key]
        newShapes[shape.index] = { ...shape, id: null, template_id: newTemplateId }
    })

    return newShapes
}

export function getClonedTemplate(template: Template, newTemplateId: ID): Template {
    const clonedTemplate = JSON.parse(JSON.stringify({ id: null, ...template }))

    return {
        id: newTemplateId,
        ...clonedTemplate,
        shapes: getClonedShapes(clonedTemplate.shapes, newTemplateId)
    }
}

export const defaultTemplateSize: Size = {
    id: null,
    name: null,
    project_id: projectId,
    width: 1080,
    height: 1080,
}

export function templateFromGalleryToStore(galleryTemplate: TemplateGalleryType): void {
    const state = store.getState() as StateType

    const form = selectFormValues(state, getFormName(state.isAlt))
    const formName = getFormName(state.isAlt)

    const sizes: SizeItem[] = state.sizes.data

    const unserializedTemplate = JSON.parse(galleryTemplate.serialized_template) as Template

    const currentFormSize = form.size_id ? find(sizes, (size: SizeItem) => {
        return size?.id === form.size_id
    }) : null

    const importedTemplateSize = unserializedTemplate.size ? unserializedTemplate.size : defaultTemplateSize

    let newCollageLayout
    let updatedImages: CollageLayoutImageType[] = []

    let newCollageLayoutsForm: CollageLayoutType[] = {
        ...selectFormValues(state, 'collageLayoutsForm')
    }

    try {
        if (unserializedTemplate.collage_layout) {
            if (!unserializedTemplate.collage_layout.id) {
                const newIndex = uniqueId()
                unserializedTemplate.collage_layout.images.forEach((image: CollageLayoutImageType) => {
                    const newImageIndex = uniqueId()
                    updatedImages[newImageIndex] = {
                        ...image,
                        id: newImageIndex,
                    }
                })
                newCollageLayout = {
                    id: newIndex,
                    project_id: projectId,
                    ...unserializedTemplate.collage_layout,
                    images: updatedImages,
                }
                //store.dispatch(updateInputValue('collageLayoutsForm', `${newIndex}`, newCollageLayout))   //not working when there is an empty collageLayoutsForm

                newCollageLayoutsForm[`${newIndex}`] = newCollageLayout
                store.dispatch(initForm('collageLayoutsForm', newCollageLayoutsForm))
            } else if (`${unserializedTemplate.collage_layout.id}`.startsWith('_')) {
                newCollageLayout = unserializedTemplate.collage_layout
                const newIndex = newCollageLayout.id
                newCollageLayoutsForm[`${newIndex}`] = newCollageLayout
                store.dispatch(initForm('collageLayoutsForm', newCollageLayoutsForm))
            }
        }
    } catch (error) {
        console.error('templateGalleryToStoreError: collage issue', error)
    }


    const newSize = currentFormSize ? currentFormSize : defaultTemplateSize
    const updatedTemplateForm: Template = {
        ...form,
        ...unserializedTemplate,
        groups: unserializedTemplate.groups ?? [],
        shapes: unserializedTemplate.shapes,
        size: { ...newSize, project_id: projectId },
        size_id: newSize.id,
        queries: form.queries,
        effects: form.effects ?? { ...emptyEffects, project_id: form.project_id },
        collage_layout: newCollageLayout ? newCollageLayout : unserializedTemplate.collage_layout,
        collage_layout_id: newCollageLayout && newCollageLayout.id ? newCollageLayout.id : null,
        active: form.active,
        name: trans('new_template'),
    }

    if (importedTemplateSize && form.size_id && form.size_id !== importedTemplateSize.id) {
        store.dispatch(initForm(formName, recalculateShapes(importedTemplateSize, form.size, updatedTemplateForm)))
    } else {
        store.dispatch(initForm(formName, updatedTemplateForm))
    }

    //add sourceImage layer and reorder:
    const newOrder = sortBy(unserializedTemplate.shapes, [(shape) => {
        return shape.priority
    }]).map(shape => shape.index)

    reorderShapes(unserializedTemplate.shapes, newOrder)
}

export function isTemplateSaved(): boolean {
    const isEdit = location.pathname.endsWith('/edit') || location.pathname.includes('/edit-alt/') || location.pathname.endsWith('/new')

    if (!isEdit) {
        return true;
    }

    const state = store.getState() as StateType
    const form = selectFormValues(state, getFormName(state.isAlt))

    const undoRedoHistory: UndoRedoHistory = state?.undoRedoHistory
    const initialTemplateForm: TemplateOrAlternativeTemplateType = undoRedoHistory?.initial
    const isDiff = initialTemplateForm && form ? isDifferentTemplateform(initialTemplateForm, form) : null

    return !undoRedoHistory || (initialTemplateForm && !isDiff)
}

export function isDifferentShapes(shapes1: Shape[], shapes2: Shape[]): boolean {
    let isDiff = false
    const keys = [...new Set([...Object.keys(shapes1), ...Object.keys(shapes2)])]

    keys.forEach((shapeIndex: string) => {
        if (shapeIndex !== SOURCE_IMAGE_INDEX) {
            shapeCheckFields.forEach(field => {
                if (shapes1[shapeIndex] && shapes2[shapeIndex] && typeof shapes2[shapeIndex][field] === "undefined" && typeof shapes1[shapeIndex][field] === "undefined") {
                    //both undefined, nothing to check
                } else if (!shapes1[shapeIndex] || !shapes2[shapeIndex] || typeof shapes1[shapeIndex][field] === "undefined" || typeof shapes2[shapeIndex][field] === "undefined") {
                    //some of them are undefined, so it diffs
                    //console.debug(`DEBUG: different shape ${shapeIndex} ${field} UNDEFINED`, shapes1, shapes2)
                    isDiff = true
                    return isDiff
                } else if (shapes1[shapeIndex] && shapes2[shapeIndex] &&
                    typeof shapes1[shapeIndex][field] !== "undefined" &&
                    typeof shapes2[shapeIndex][field] !== "undefined" &&
                    JSON.stringify(shapes1[shapeIndex][field]) !== JSON.stringify(shapes2[shapeIndex][field])
                ) {
                    //different value, check if it is dynamic text type
                    if (!(field === 'text_content' && ![DYNAMIC_TEXT_TYPE, TEXT_WITH_ELEMENTS_TYPE, IMAGE_FROM_ELEMENTS_TYPE].includes(shapes1[shapeIndex].type))) {
                        //console.debug(`DEBUG: different shape ${shapeIndex} ${field} (${shapes1[shapeIndex].type})`, shapes1[shapeIndex][field], shapes2[shapeIndex][field])
                        isDiff = true
                        return isDiff
                    }
                }
            });
        }

        if (isDiff) {
            return true
        }
    })

    return isDiff
}

export function isDifferentTemplateform(templateForm1: TemplateOrAlternativeTemplateType, templateForm2: TemplateOrAlternativeTemplateType): boolean {
    if (!templateForm1 || !templateForm2 || templateForm1.id !== templateForm2.id) {
        return false
    }

    let diffFields = null
    templateCheckFields.forEach(field => {
        if (
            typeof templateForm1[field] !== typeof templateForm2[field] ||
            (typeof templateForm1[field] !== "undefined" && typeof templateForm2[field] !== "undefined" && JSON.stringify(templateForm1[field]) !== JSON.stringify(templateForm2[field]))
        ) {
            //console.debug(`DEBUG: ${field} diffs: ${templateForm1[field]} vs ${templateForm2[field]} id: ${templateForm1.id} vs ${templateForm2.id}`)

            diffFields = true
            return true
        }
    })

    if (diffFields) {
        return true
    }

    //check shapes:
    if (typeof templateForm1.shapes !== "undefined" && typeof templateForm2.shapes !== "undefined") {
        const diffShapes = isDifferentShapes(templateForm1.shapes, templateForm2.shapes)

        if (diffShapes) {
            diffFields = true
        }
    }

    if (diffFields) {
        return true
    }

    //check effects:
    if (typeof templateForm1.effects !== "undefined" || typeof templateForm2.effects !== "undefined") {
        const effects1 = templateForm1.effects ?? { ...emptyEffects, project_id: templateForm1.project_id }
        const effects2 = templateForm2.effects ?? { ...emptyEffects, project_id: templateForm2.project_id }
        const diffEffects = isDifferentEffects(effects1, effects2)

        return diffEffects
    }

    return false
}

export function isDifferentEffects(effects1: EffectsType, effects2: EffectsType): boolean {
    let diffEffects = null
    Object.keys(effects1).forEach(field => {
        if (
            typeof effects1[field] !== typeof effects2[field] ||
            (typeof effects1[field] !== "undefined" && typeof effects2[field] !== "undefined" && JSON.stringify(effects1[field]) !== JSON.stringify(effects2[field]))
        ) {
            //console.debug(`DEBUG: effects ${field} diffs: ${effects1[field]} vs ${effects2[field]} id: ${effects1.id} vs ${effects2.id}`)

            diffEffects = true
            return true
        }
    })

    return diffEffects;
}



export function removeEmpty(obj) {
    return Object.keys(obj)
        .filter(function (k) {
            return obj[k] != null;
        })
        .reduce(function (acc, k) {
            const isArray = Array.isArray(obj[k])
            acc[k] = typeof obj[k] === "object" && !isArray ? removeEmpty(obj[k]) : obj[k];
            return acc;
        }, {});
}

export function getTemplateGradientAndBackground(form: TemplateOrAlternativeTemplateType, randomProduct: RandomProduct, gradients: Gradient[]) {
    const randomProductId = randomProduct?.id || 1
    const dominantColor = randomProduct?.dominantColor
    const randomProductPalette = randomProduct?.palette && Array.isArray(randomProduct.palette) && randomProduct.palette.length > 1 ? randomProduct.palette : null

    let templateBackgroundColor = '#ffffff'
    let templateGradient = null

    switch (form.background_type) {
        case 'color':
            templateBackgroundColor = form.background
            break;
        case 'dominant_color':
            templateBackgroundColor = dominantColor ? dominantColor : templateBackgroundColor
            break;
        case 'dominant_color_2':
            templateBackgroundColor = randomProductPalette ? randomProductPalette[0] : templateBackgroundColor
            break;
        case 'dominant_color_3':
            templateBackgroundColor = randomProductPalette ? randomProductPalette[1] : templateBackgroundColor
            break;
        case 'gradient':
            templateGradient = form?.gradient_id && gradients ? find(gradients, (g: Gradient) => {
                return g.id === parseInt(`${form.gradient_id}`)
            }) : null
            break;
        case 'random_colors':
            const randomColors = form.background_colors?.split(',')
            const countColors = Array.isArray(randomColors) ? randomColors.length : 0
            if (countColors > 0) {
                templateBackgroundColor = randomColors[randomProductId % countColors]
            }
            break;
        case 'random_gradients':
            const randomGradients = form.background_gradient_ids?.split(',')
            const countGradients = Array.isArray(randomGradients) ? randomGradients.length : 0
            if (countGradients > 0) {
                const randomGradientId = randomGradients[randomProductId % countGradients]
                templateGradient = randomGradientId ? find(gradients, (g: Gradient) => {
                    return g.id === parseInt(`${randomGradientId}`)
                }) : null
            }
            break;
        default:
            templateGradient = form.gradient_id ? find(gradients, (g: Gradient) => {
                return g.id === parseInt(`${form.gradient_id}`)
            }) : null
            templateBackgroundColor = form.background || '#ffffff'
            break;
    }

    return {
        templateBackgroundColor: templateBackgroundColor,
        templateGradient: templateGradient
    }
}

export const NO_IMAGE_URL = `${URL.laravelBase}img/icons/no-image.svg`

export function getShapeSize(shape: Shape): SizeType {
    const scale = shape.scale ? shape.scale : 1

    const width = (shape.width || shape.height) * scale
    const height = (shape.height || shape.width) * scale

    const templateSize = getTemplateSize()

    switch (shape.type) {
        case DASHED_FRAME_TYPE:
        case SOURCE_IMAGE_TYPE:
            return {
                width: templateSize.width,
                height: templateSize.height,
            }
        case TRIANGLE1_TYPE:
            return {
                width: width,
                height: width * 0.8660254038,
            }
        case TRIANGLE2_TYPE:
            return {
                width: width,
                height: width * 0.8660254038,
            }
        default:
            return {
                width: width,
                height: height,
            }
    }
}

export function hasClass(el: Element, className: string): boolean {
    if (el.classList)
        return el.classList.contains(className);
    return !!el.className.match(new RegExp('(\\s|^)' + className + '(\\s|$)'));
}

export function addClass(el: Element, className: string): void {
    if (el.classList)
        el.classList.add(className)
    else if (!hasClass(el, className))
        el.className += " " + className;
}

export function removeClass(el: Element, className: string): void {
    if (el.classList)
        el.classList.remove(className)
    else if (hasClass(el, className)) {
        var reg = new RegExp('(\\s|^)' + className + '(\\s|$)');
        el.className = el.className.replace(reg, ' ');
    }
}

export const ALL_OUTPUT_FORMATS_WITHOUT_GIF = [
    'jpeg',
    'webp',
    'png',
]

export const COMPRESSIBLE_FORMATS = [
    'jpeg',
    'webp',
];

export const ICONS_VERSION = 1

export function getSVGPathFromPoints(points: PathPoint[],
    activePointKey: number | null = null,
    dx: number = 0,
    dy: number = 0,
    dcx: number = 0,
    dcy: number = 0,
    dcx2: number = 0,
    dcy2: number = 0
): string {
    return points.map((point: PathPoint, key: number) => {
        const isActivePoint = key === activePointKey
        const xpos = round(point.x + (isActivePoint ? dx : 0))
        const ypos = round(point.y + (isActivePoint ? dy : 0))

        const cx = round(point.cx + (isActivePoint ? dcx : 0))
        const cy = round(point.cy + (isActivePoint ? dcy : 0))

        const cx2 = (typeof point.cx2 !== 'undefined' && point.cx2 !== null) ? round(point.cx2 + (isActivePoint ? dcx2 : 0)) : null
        const cy2 = (typeof point.cy2 !== 'undefined' && point.cy2 !== null) ? round(point.cy2 + (isActivePoint ? dcy2 : 0)) : null

        const t = (key === 0) ? 'M' : (point.t ?? DEFAULT_PATH_TYPE)

        const xAxisRotationDegrees = point.xRotDegrees ? point.xRotDegrees : 0
        const largeArcFlag = point.isLargeArc ? 1 : 0
        const sweepFlag = point.isSweep ? 1 : 0

        switch (t) {
            case 'L':
            case 'M':
            case 'T':
                return `${t} ${xpos},${ypos}`
            case 'H':
                return `${t} ${xpos}`
            case 'V':
                return `${t} ${ypos}`
            case 'A':
                //A rx ry x-axis-rotation large-arc-flag sweep-flag x y
                return `${t} ${cx},${cy} ${xAxisRotationDegrees} ${largeArcFlag} ${sweepFlag} ${xpos},${ypos}`
            case 'C':
                if (cx2 !== null && cy2 !== null) {
                    return `${t} ${cx},${cy} ${cx2},${cy2} ${xpos},${ypos}`
                } else {
                    return `S ${cx},${cy} ${xpos},${ypos}`
                }
            case 'S':
            default:
                return `${t} ${cx},${cy} ${xpos},${ypos}`
        }
    }).join(' ')
}

export function getHashesListFromFindSimilar(findSimilar: FindSimilarType): string[] {
    if (!findSimilar || !findSimilar.similarProducts || Object.keys(findSimilar.similarProducts).length == 0) {
        return null
    }

    let onlyHashes = []
    Object.keys(findSimilar.similarProducts).forEach(imageHash => {
        findSimilar.similarProducts[imageHash].forEach(h => {
            onlyHashes.push(h)
        })
    })

    return onlyHashes
}

export function createNewTemplateForm(type: AITemplatType): void {

    let newTemplateForm: Template = newTemplate()
    newTemplateForm.type = type;

    store.dispatch(initForm('templateForm', { ...newTemplateForm }))
}

export function randomIntFromInterval(min: number, max: number): number { // min and max included
    return Math.floor(Math.random() * (max - min + 1) + min);
}

export function getMoveCursorUrl(angle: number): string {
    const s = 24
    const strokeWidth = 2

    const d = [
        "M40 20q0 .6-.4 1l-5.7 5.7",
        "q-.5.4-1 .4t-1-.4-.5-1v-2.8h-8.5v8.5h2.8",
        "q.6 0 1 .5t.4 1-.4 1L21 39.6",
        "q-.4.4-1 .4t-1-.4l-5.7-5.7",
        "q-.4-.5-.4-1t.4-1 1-.5h2.8v-8.5H8.6v2.8",
        "q0 .6-.5 1t-1 .4-1-.4L.4 21",
        "q-.4-.4-.4-1t.4-1l5.7-5.7",
        "q.5-.4 1-.4t1.1.4.4 1v2.8h8.5V8.6h-2.8",
        "q-.6 0-1-.5t-.4-1 .4-1L19 .4",
        "q.4-.4 1-.4t1 .4l5.7 5.7",
        "q.4.5.4 1t-.4 1-1 .5h-2.8v8.5h8.5v-2.8",
        "q0-.6.5-1t1-.4 1 .4l5.7 5.7",
        "q.4.4.4 1z"
    ]
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="-3 -3 45 45" width="${s}px" height="${s}px">` +
        `<g transform="rotate(${angle}, ${s / 2}+3, ${s / 2})">` +
        `<path stroke-width="${strokeWidth}" fill="#ffffff" stroke="#000000" stroke-linejoin="miter" d="${d.join(' ')}" style="paint-order: stroke fill markers;" />` +
        `</g>` +
        `</svg>`

    return `url("data:image/svg+xml;base64,${btoa(svg)}") ${s / 2} ${s / 2}`
}

export function getResizeCursorUrl(angle: number): string {
    const strokeWidth = 1
    const s = 24
    const d = [
        "M15.001,8.999",
        "L9.004, 8.999",
        "L9.00498491, 6.74982882",
        "C9.00498491, 6.09013561 8.21462601, 5.75179761 7.73729401, 6.20715384",
        "L2.23356691, 11.4574962",
        "C1.92381447, 11.752988 1.92382252, 12.2473813 2.23358458, 12.542863",
        "L7.73731168, 17.792863",
        "L7.81957701, 17.8614839",
        "C8.30002573, 18.2096102 9.00498491, 17.8732037 9.00498491, 17.2501712",
        "L9.004, 15 L15.001, 15 L15.0015761, 17.2501712",
        "C15.0015761, 17.9100665 15.792351, 18.2483191 16.2695726, 17.7925545",
        "L21.7667386, 12.5425545",
        "C22.0760697, 12.2471317 22.0760778, 11.7532376 21.7667563, 11.4578047",
        "L16.2695902, 6.20746235",
        "C15.792375, 5.75167418 15.0015761, 6.08992183 15.0015761, 6.74982882",
        "L15.001, 8.999 Z"
    ]
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' xmlns:xlink="http://www.w3.org/1999/xlink" width="${s}px" height="${s}px">` +
        `<g transform="rotate(${angle}, ${s / 2}, ${s / 2})">` +
        `<path stroke-width="${strokeWidth}" fill="#ffffff" stroke="#000000" d="${d.join(' ')}" />` +
        `</g>` +
        `</svg>`

    return `url("data:image/svg+xml;base64,${btoa(svg)}") ${s / 2} ${s / 2}`
}

export type CursorType = "top" | "left" | "right" | "bottom" | "rotate" | "move" | "topleft" | "topright" | "bottomleft" | "bottomright" | "center" | "center-box" | "image"
export function getCursorByType(type: CursorType, angle: number = 0) {
    switch (type) {
        case 'top':
        case 'bottom':
            return `${getResizeCursorUrl(angle + 90)}, ns-resize`
        case 'left':
        case 'right':
            return `${getResizeCursorUrl(angle)}, ew-resize`
        case 'topright':
            return `${getResizeCursorUrl(angle - 45)}, ne-resize`
        case 'topleft':
            return `${getResizeCursorUrl(angle + 45)}, nw-resize`
        case 'bottomleft':
            return `${getResizeCursorUrl(angle - 45)}, sw-resize`
        case 'bottomright':
            return `${getResizeCursorUrl(angle + 45)}, se-resize`
        case 'rotate':
            return ROTATE_CURSOR;
        case 'move':
            return 'grabbing';
        case 'center':
        //case 'move':
        case 'image':
        case 'center-box':
            return `${getMoveCursorUrl(angle)}, crosshair`
        //return `${getMoveCursorUrl(angle)}, ${ALL_SCROLL_CURSOR}`
        default:
            return DEFAULT_CURSOR;
    }
}

export type GradientUsageType = {
    gradientId: ID
    templateId: ID
}

export function checkGradientsUsage(templates: ReduxList<Template>): GradientUsageType[] {
    let gradientUsage: GradientUsageType[] = []

    if (templates.isLoaded && templates.data) {
        templates.data.forEach((t: Template) => {
            if (t.gradient_id) {
                gradientUsage.push({
                    templateId: t.id,
                    gradientId: t.gradient_id
                })
            }
            if (t.background_gradient_ids && t.background_gradient_ids !== '') {
                t.background_gradient_ids.split(',').forEach(gradientId => {
                    gradientUsage.push({
                        templateId: t.id,
                        gradientId: gradientId
                    })
                })
            }
            if (t.shapes) {
                Object.keys(t.shapes).forEach((shapeIndex: ID) => {
                    const s = t.shapes[shapeIndex]
                    if (s.gradient_id) {
                        gradientUsage.push({
                            templateId: t.id,
                            gradientId: s.gradient_id
                        })
                    }
                })
            }
        })

        return gradientUsage
    }
}

export function setGuidesFromSizeOrRandomProduct(size: Size | null, randomProduct: RandomProduct | null) {
    const width = size?.width || randomProduct?.width || null
    const height = size?.height || randomProduct?.height || null

    if (width && height) {
        const minApproach = getMinApproachFromSize(width, height)
        store.dispatch(setGuides({
            ...initialGuidesState,
            minApproachX: minApproach.x,
            minApproachY: minApproach.y,
            gridStep: getGridStepSize(width, height, initialGuidesState.gridStepMode)
        }))
    }
}

export const getFormName = (isAlt: boolean): FormNameType => {
    return isAlt ? 'altTemplateForm' : 'templateForm'
}

export function getInpaintingGroupPosition(inpaintingGroup: InpaintingGroupType): string {
    const position = inpaintingGroup.pos_relative_to ? `${inpaintingGroup.pos_relative_to}` : 'a';
    const scaleObjects = inpaintingGroup.scale_objects ? 's' : 'k';

    return `${position}_${scaleObjects}`
}

export function getAltImageFromRandomProduct(randomProduct: RandomProduct, levels: ID[], levelsMode: LevelsModeType): string {
    if (!randomProduct || !randomProduct.alternative_images || !randomProduct.alternative_images.length) {
        return null
    }

    const c = randomProduct.alternative_images.length

    switch (levelsMode) {
        case LEVELS_MODE_LAST:
            const l = find(randomProduct.alternative_images, (altImage) => `${altImage.level}` === `${c - 1}`)
            return l?.url
        case LEVELS_MODE_SELECTED:
            if (levels) {
                const results = levels.map((level) => {
                    return find(randomProduct.alternative_images, (altImage) => `${altImage.level}` === `${level}`)
                })
                return results[0]?.url || null
            }
        case LEVELS_MODE_FIRST:
        case LEVELS_MODE_ALL:
        default:
            const f = find(randomProduct.alternative_images, (altImage) => `${altImage.level}` === `0`)
            return f?.url
    }

    return null;
}

export const canBeOutpaintedOver = (outpaintOver: boolean | intboolean, overscan: number, translateX: number, translateY: number): boolean => {
    return !!(outpaintOver && ((overscan && overscan < 0) || translateX || translateY))
}

export const getAddOutpaintedSuffix = (templateIsOutpaintOver: boolean | intboolean, templateOverscan: number, templateTranslateX: number, templateTranslateY: number): string => {
    let addSuffix = ''

    if (templateIsOutpaintOver && templateOverscan && templateOverscan < 0) {
        addSuffix += `_${templateOverscan}`

        if (templateTranslateX || templateTranslateY) {
            addSuffix += `_${templateTranslateX}_${templateTranslateY}`
        }
    }

    return addSuffix
}

export const canBeOutpainted = (imageWidth: number, imageHeight: number, templateSize: SizeType, overscan: number, isOutpaintOver: boolean): boolean => {
    if (!imageWidth || !imageHeight) {
        return false;
    }

    if (templateSize && templateSize.width && templateSize.height) {
        if (isOutpaintOver) {
            return true;    //the output size is $templateSize
        }

        const newWidth = templateSize.width + 2 * overscan
        const newHeight = templateSize.height + 2 * overscan

        if (newHeight <= 0 || newWidth <= 0) {
            return false;
        }

        const r1 = newWidth / newHeight;
        const r2 = imageWidth / imageHeight;

        if (round(r1 * 100) / 100 !== round(r2 * 100) / 100) {   //ve workerech LABu je tolerance 3%!!!
            return true;
        }
    } else if (imageWidth !== imageHeight) {   //if three is not a new size, outpainting is done for square... if not already square.
        return true;
    }

    return false;
}

export const getNewSize = (imageWidth: number, imageHeight: number, templateSize: SizeType, templateOverscan: number, isOutpaintOver: boolean): SizeType | null => {
    if (!canBeOutpainted(imageWidth, imageHeight, templateSize, templateOverscan, isOutpaintOver)) {
        return null
    }

    const sizeFromTemplate = templateSize && templateSize.width && templateSize.height
    const maxSide = max(imageWidth, imageHeight);

    if (!sizeFromTemplate) {
        return { width: maxSide, height: maxSide }
    }

    let newWidth: number = templateSize.width
    let newHeight: number = templateSize.height

    if (!isOutpaintOver) {
        newWidth += 2 * templateOverscan
        newHeight += 2 * templateOverscan
    }

    return {
        width: newWidth,
        height: newHeight,
    }
}

export const getSelectedAltLevels = (): { levels: string[], mode: LevelsModeType } => {
    const state = store.getState() as StateType

    const l = window.location.pathname      //or location.href??
    const a = l.split('/')
    const altTemplateId = a[a.indexOf('edit-alt') + 1]  //right after edit-alt
    const templateId = a[a.indexOf('templates') + 1]  //right after templates

    const levelsForTemplate = (state.isAlt && state.alternativeTemplatesLevels?.data ?
        find(state.alternativeTemplatesLevels?.data, (altTemplateLevels: AlternativeTemplatesLevels) => {
            return `${altTemplateLevels.alternative_template_id}` === `${altTemplateId}` &&
                `${altTemplateLevels.template_id}` === `${templateId}`
        }) : null) as AlternativeTemplatesLevels

    if (levelsForTemplate) {

        return {
            levels: levelsForTemplate.levels ? sortBy(levelsForTemplate.levels.split(',')) : [],
            mode: levelsForTemplate.levels_mode
        }
    }

    return null
}

export const getAltTemplateLevelsByLevel = (altTemplatesLevels: AlternativeTemplatesLevels[], countAvailableAltLevels: number): (AlternativeTemplatesLevels | null)[] => {
    //level to alt template id:
    let altTemplateLevelsByLevel = new Array(countAvailableAltLevels).fill(null)

    for (let currentLevel = 0; currentLevel < countAvailableAltLevels; currentLevel++) {
        altTemplateLevelsByLevel[currentLevel] = null;

        forEach(altTemplatesLevels, (altTemplateLevels: AlternativeTemplatesLevels) => {
            const levelsMode = altTemplateLevels.levels_mode
            const selectedLevels = levelsMode === LEVELS_MODE_SELECTED && altTemplateLevels.levels ? altTemplateLevels.levels.split(',') : []
            if (
                (altTemplateLevelsByLevel[currentLevel] === null) && (    //apply only once on one level
                    (levelsMode === LEVELS_MODE_ALL) ||
                    (currentLevel === 0 && levelsMode === LEVELS_MODE_FIRST) ||
                    (currentLevel === countAvailableAltLevels - 1 && levelsMode === LEVELS_MODE_LAST) ||
                    (levelsMode === LEVELS_MODE_SELECTED && selectedLevels.includes(`${currentLevel}`))
                )
            ) {
                altTemplateLevelsByLevel[currentLevel] = altTemplateLevels
            }
        })
    }

    return altTemplateLevelsByLevel
}

export const getAltTemplatesByLevels = (altTemplatesLevels: AlternativeTemplatesLevels[], alternativeTemplates: AlternativeTemplate[], countAvailableAltLevels: number): (AlternativeTemplate | null)[] => {
    const c = getAltTemplateLevelsByLevel(altTemplatesLevels, countAvailableAltLevels)

    return c.map((m: AlternativeTemplatesLevels, i: number) => {
        if (!m) { return null }

        return find(alternativeTemplates, (a: AlternativeTemplate) => {
            return `${m.alternative_template_id}` === `${a.id}`
        })
    })
}

export function getFontsFromForm(): string[] {
    const state = store.getState() as StateType
    const form = selectFormValues(state, getFormName(state.isAlt))

    let currentFonts = []
    if (form.shapes) {
        forEach(form.shapes, (shape: Shape) => {
            if (shape.text_fontfamily && !currentFonts.includes(shape.text_fontfamily)) {
                currentFonts.push(shape.text_fontfamily)
            }
        })
    }

    return currentFonts
}

export function getLastUsedFont(): string {
    const state = store.getState() as StateType
    const fontsInProject = state.fontsInProject

    return fontsInProject && fontsInProject.length > 0 ? fontsInProject[fontsInProject.length - 1] : defaultFontName
}